import { baseApiClient as api } from "../baseService";

export const addTagTypes = ["entity-changes"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      entityChangesList: build.query<
        EntityChangesListApiResponse,
        EntityChangesListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/entity-changes/`,
          params: {
            firm: queryArg.firm,
            limit: queryArg.limit,
            matter_id: queryArg.matterId,
            matter_name: queryArg.matterName,
            matter_status: queryArg.matterStatus,
            case_manager: queryArg.caseManager,
            offset: queryArg.offset,
            ordering: queryArg.ordering,
          },
        }),
        providesTags: ["entity-changes"],
      }),
      entityChangesRetrieve: build.query<
        EntityChangesRetrieveApiResponse,
        EntityChangesRetrieveApiArg
      >({
        query: (queryArg) => ({ url: `/api/entity-changes/${queryArg.id}/` }),
        providesTags: ["entity-changes"],
      }),
      entityChangesAuditLogs: build.query<
        EntityChangesAuditLogsApiResponse,
        EntityChangesAuditLogsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/entity-changes/${queryArg.id}/audit-logs/`,
        }),
        providesTags: ["entity-changes"],
      }),
      entityChangesBulkUpdateStatus: build.mutation<
        EntityChangesBulkUpdateStatusApiResponse,
        EntityChangesBulkUpdateStatusApiArg
      >({
        query: (queryArg) => ({
          url: `/api/entity-changes/bulk-update-status/`,
          method: "POST",
          body: queryArg.bulkEntityChangeStatusRequestSchema,
        }),
        invalidatesTags: ["entity-changes"],
      }),
      entityChangesFilterOptions: build.query<
        EntityChangesFilterOptionsApiResponse,
        EntityChangesFilterOptionsApiArg
      >({
        query: () => ({ url: `/api/entity-changes/filter-options/` }),
        providesTags: ["entity-changes"],
      }),
      entityChangesMatterDetail: build.query<
        EntityChangesMatterDetailApiResponse,
        EntityChangesMatterDetailApiArg
      >({
        query: (queryArg) => ({
          url: `/api/entity-changes/matter-detail/${queryArg.matterId}/`,
        }),
        providesTags: ["entity-changes"],
      }),
      entityChangesBlobSinkActionsApprove: build.mutation<
        BulkBlobSinkActionResponseSchema,
        { bulkBlobSinkActionRequestSchema: BulkBlobSinkActionRequestSchema }
      >({
        query: (queryArg) => ({
          url: `/api/entity-changes/blob-sink-actions/approve/`,
          method: "POST",
          body: queryArg.bulkBlobSinkActionRequestSchema,
        }),
        invalidatesTags: ["entity-changes"],
      }),
      entityChangesBlobSinkActionsReject: build.mutation<
        BulkBlobSinkActionResponseSchema,
        {
          bulkBlobSinkActionRejectRequestSchema: BulkBlobSinkActionRejectRequestSchema;
        }
      >({
        query: (queryArg) => ({
          url: `/api/entity-changes/blob-sink-actions/reject/`,
          method: "POST",
          body: queryArg.bulkBlobSinkActionRejectRequestSchema,
        }),
        invalidatesTags: ["entity-changes"],
      }),
      entityChangesBlobSinkActionsRetry: build.mutation<
        BulkBlobSinkActionResponseSchema,
        { bulkBlobSinkActionRequestSchema: BulkBlobSinkActionRequestSchema }
      >({
        query: (queryArg) => ({
          url: `/api/entity-changes/blob-sink-actions/retry/`,
          method: "POST",
          body: queryArg.bulkBlobSinkActionRequestSchema,
        }),
        invalidatesTags: ["entity-changes"],
      }),
      entityChangesBlobSinkActionUpdateTarget: build.mutation<
        BlobSinkActionSchema,
        {
          actionId: string;
          updateBlobSinkActionTargetSchema: UpdateBlobSinkActionTargetSchema;
        }
      >({
        query: (queryArg) => ({
          url: `/api/entity-changes/blob-sink-actions/${queryArg.actionId}/target/`,
          method: "PATCH",
          body: queryArg.updateBlobSinkActionTargetSchema,
        }),
        invalidatesTags: ["entity-changes"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type EntityChangesListApiResponse =
  /** status 200  */ PaginatedEntityChangeSummarySchemaList;
export type EntityChangesListApiArg = {
  /** Filter by firm ID(s). Supports comma-separated UUIDs. */
  firm?: string[];
  /** Page size (default 50, max 200). */
  limit?: number;
  /** Filter by matter ID(s). Supports comma-separated UUIDs. */
  matterId?: string[];
  /** Case-insensitive search on matter name. */
  matterName?: string;
  /** Filter by matter status(es). Supports comma-separated values. */
  matterStatus?: string[];
  /** Filter by case manager user ID(s). Supports comma-separated UUIDs. */
  caseManager?: string[];
  /** Starting index for pagination. */
  offset?: number;
  /** Ordering clause (comma-separated). Supports: total_changes, earliest_change_at, name, firm__name. */
  ordering?: string;
};
export type EntityChangesRetrieveApiResponse =
  /** status 200  */ EntityChangeSchema;
export type EntityChangesRetrieveApiArg = {
  /** EntityChange ID. */
  id: string;
};
export type EntityChangesAuditLogsApiResponse =
  /** status 200  */ EntityChangesAuditLogSchema;
export type EntityChangesAuditLogsApiArg = {
  /** EntityChange ID. */
  id: string;
};
export type EntityChangesBulkUpdateStatusApiResponse =
  /** status 200  */ BulkEntityChangeStatusResponseSchema;
export type EntityChangesBulkUpdateStatusApiArg = {
  bulkEntityChangeStatusRequestSchema: BulkEntityChangeStatusRequestSchema;
};
export type EntityChangesFilterOptionsApiResponse =
  /** status 200  */ EntityChangesFilterOptionsSchema;
export type EntityChangesFilterOptionsApiArg = void;
export type EntityChangesMatterDetailApiResponse =
  /** status 200  */ MatterEntityChangesDetailSchema;
export type EntityChangesMatterDetailApiArg = {
  /** Matter ID. */
  matterId: string;
};
export type EntityCategory =
  | "PROVIDER"
  | "TREATMENT_REQUEST"
  | "COVERAGE"
  | "TASK"
  | "TASK_UPDATE"
  | "STAFF_NOTE"
  | "DOCUMENT"
  | "CASE_EXPENSE"
  | "OTHER";
export type EntityCategoryCounts = {
  category: EntityCategory;
  count: number;
};
export type EntityChangeSummarySchema = {
  matter_id: string | string;
  matter_name: string;
  matter_status: string;
  firm_name: string;
  total_changes: number;
  category_counts: EntityCategoryCounts[];
  earliest_change_at: string | null;
  oldest_pending_change?: string | null;
  case_manager_name?: string | null;
};
export type PaginatedEntityChangeSummarySchemaList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: EntityChangeSummarySchema[];
};
export type CaseExpenseType = "POLICE" | "MEDICAL" | "MAIL" | "OTHER";
export type CaseExpenseStatus = "UNPAID" | "SENT" | "PAID" | "CANCELLED";
export type PaymentMethod = "CREDIT_CARD" | "CHEQUE" | "FIRM_PAYS";
export type LabeledPhone = {
  /** Label like 'mobile', 'home', 'work', 'main', 'fax' */
  label: string;
  /** Phone number */
  value: string;
};
export type LabeledEmail = {
  /** Label like 'home', 'work', 'other' */
  label: string;
  /** Email address */
  value: string;
};
export type LabeledPostalAddress = {
  /** Label like 'home', 'work', 'billing', 'shipping' */
  label: string;
  /** Street address (can include multiple lines) */
  street?: string | null;
  /** City */
  city?: string | null;
  /** State/Province */
  state?: string | null;
  /** Postal/ZIP code */
  postal_code?: string | null;
  /** Country */
  country?: string | null;
};
export type SimpleContactInfo = {
  /** Organization/company name */
  organization_name?: string | null;
  /** First name (for individuals) */
  given_name?: string | null;
  /** Last name (for individuals) */
  family_name?: string | null;
  /** Phone numbers with labels (mobile, home, work, main, fax) */
  phone_numbers?: LabeledPhone[];
  /** Email addresses with labels (home, work, other) */
  email_addresses?: LabeledEmail[];
  /** Postal addresses with labels (home, work, billing, shipping) */
  postal_addresses?: LabeledPostalAddress[];
  /** Additional notes about this contact */
  note?: string | null;
};
export type ContactPayee = {
  type?: "CONTACT";
  contact_info: SimpleContactInfo;
};
export type CoverageCategory =
  | "FIRST_PARTY"
  | "FIRST_PARTY_HEALTH"
  | "THIRD_PARTY"
  | "NOT_LIABLE_INVOLVED_PARTY"
  | "UNCLEAR"
  | "PROPERTY"
  | "WORKERS_COMPENSATION";
export type CoveragePayee = {
  type?: "COVERAGE";
  id: string | string;
  carrier_name: string | null;
  category: CoverageCategory;
};
export type PoliceDepartmentPayee = {
  type?: "POLICE_DEPARTMENT";
  id: string | string;
  name: string;
};
export type ProviderPayee = {
  type?: "PROVIDER";
  id: string | string;
  name: string;
};
export type BlobSummary = {
  id: string | string;
  name: string;
};
export type ReadCaseExpense = {
  id: string | string;
  matter_id: string | string;
  expense_type: CaseExpenseType;
  status: CaseExpenseStatus;
  payment_method?: PaymentMethod;
  payment_method_identifier?: string | null;
  payment_date?: string | null;
  amount: string;
  reason?: string | null;
  payee:
    | ({
        type: "CONTACT";
      } & ContactPayee)
    | ({
        type: "COVERAGE";
      } & CoveragePayee)
    | ({
        type: "POLICE_DEPARTMENT";
      } & PoliceDepartmentPayee)
    | ({
        type: "PROVIDER";
      } & ProviderPayee);
  blobs?: BlobSummary[];
  created_at: string;
  modified_at: string;
};
export type CaseExpenseEntitySnapshot = {
  entity_type?: "CASE_EXPENSE";
  data: ReadCaseExpense;
};
export type PolicyType =
  | "AUTO"
  | "HOMEOWNERS"
  | "COMMERCIAL"
  | "HEALTH"
  | "UMBRELLA"
  | "WORKERS_COMPENSATION"
  | "GENERAL_LIABILITY"
  | "OTHER";
export type ContactHandleType =
  | "PHONE"
  | "EMAIL"
  | "WEBSITE"
  | "FAX"
  | "OTHER"
  | "MAILING_ADDRESS";
export type WebSource = {
  /** The URL of the source */
  url: string;
  /** Very short display text for the source URL */
  title: string;
};
export type ContactHandleSchema = {
  id?: (string | string) | null;
  /** The captured contact information. Should contain an E.164 formatted phone number, or an email address, or a website, or a fax number. */
  value?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  handle_type: ContactHandleType;
  /** Short description of the handle; e.g. 'Billing Dept Toll-Free', 'Front Desk Fax' etc. */
  name?: string | null;
  /** Optional notes about the handle. */
  notes?: string | null;
  /** Citations for the handle. */
  sources?: WebSource[] | null;
  /** Whether this is the preferred contact handle. */
  preferred?: boolean | null;
  division_id?: (string | string) | null;
};
export type CarrierDetail = {
  /**
            The colloquial, common form, well-known name of the insurance carrier if mentioned, null otherwise.
            This is a key field for matching and deduplication.
             */
  name?: string | null;
  /**
            The BEST (at _MOST_ 2 handles per type) web pages or phone numbers for submitting and managing claims if mentioned, null otherwise.
             */
  handles?: ContactHandleSchema[] | null;
  /**
            The headquarters address of the insurance carrier if mentioned, null otherwise.
             */
  address?: string | null;
};
export type IdentifierType =
  | "POLICY_NUMBER"
  | "MEMBER_ID"
  | "GROUP_NUMBER"
  | "OTHER";
export type CoverageDataSource =
  | "UNKNOWN"
  | "BPO"
  | "USER_EDIT"
  | "SYSTEM"
  | "VOICE_AI";
export type CoverageDataSourceInfo = {
  /** The source that created or last modified this data. */
  source?: CoverageDataSource;
  /** User ID if source is USER_EDIT. */
  user_id?: string | null;
  /** User display name if source is USER_EDIT (for display purposes). */
  user_name?: string | null;
  /** When the data was added/modified from this source. */
  timestamp?: string | null;
};
export type PolicyIdentifier = {
  /**
            The type of identifier for the policy.
             */
  identifier_type: IdentifierType;
  /**
            The value of the identifier for the policy.
             */
  identifier_value: string;
  /**
            Metadata about the source of this policy identifier data (BPO, user edit, system, etc.).
            None indicates unknown/legacy data for backwards compatibility.
             */
  source?: CoverageDataSourceInfo;
};
export type BenefitType =
  | "PERSONAL_INJURY_PROTECTION"
  | "MEDICAL_PAYMENTS"
  | "UNINSURED_MOTORIST"
  | "UNDER_INSURED_MOTORIST"
  | "BODILY_INJURY_LIABILITY"
  | "PROPERTY_DAMAGE_LIABILITY"
  | "COMPREHENSIVE_AUTO"
  | "COLLISION_AUTO"
  | "WORKERS_COMPENSATION"
  | "OTHER";
export type BenefitDetail = {
  /**
            A limited categorization of insurance benefits we are interested in extracting.
            
            <benefits-type-glossary>
                PIP == PERSONAL_INJURY_PROTECTION
                MEDPAY == MEDICAL_PAYMENTS
                UM == UNINSURED_MOTORIST
                UIM == UNDER_INSURED_MOTORIST
                BI == BODILY_INJURY_LIABILITY
                COMPREHENSIVE == COMPREHENSIVE_AUTO
                COLLISION == COLLISION_AUTO
                anything else == OTHER
            </benefits-type-glossary>
              
             */
  benefit_type?: BenefitType;
  /**
            Structured map of coverage limitation labels to their numeric dollar amounts.
            Keys describe the limit type (e.g., "per person", "per accident", "bodily injury per person").
            Values MUST be integers representing dollar amounts (e.g., 25000 for $25,000).
    
            IMPORTANT: Do NOT use slash notation like "25000/50000". Instead, create SEPARATE entries:
            - WRONG: {"bodily injury": "25000/50000"}
            - CORRECT: {"bodily injury per person": 25000, "bodily injury per accident": 50000}
    
            Examples:
            - {"per person": 100000, "per accident": 300000}
            - {"BI per person": 25000, "BI per accident": 50000, "PD": 25000}
            - {"medical payments": 5000}
             */
  limitations?: {
    [key: string]: number;
  } | null;
  /**
            Whether stacking benefits apply for UM/UIM coverage. When true, coverage limits from
            multiple policies can be combined. Only applicable for UNINSURED_MOTORIST and
            UNDER_INSURED_MOTORIST benefit types. Null indicates unknown/not specified.
             */
  is_stacked?: boolean | null;
  /**
            Metadata about the source of this benefit data (BPO, user edit, system, etc.).
            None indicates unknown/legacy data for backwards compatibility.
             */
  source?: CoverageDataSourceInfo;
};
export type AdjusterDetail = {
  /**
            The benefit the adjuster is specifically assigned to, if explicitly stated, otherwise null.
             */
  benefit_type?: BenefitType;
  /**
            Name of the adjuster handling a related claim if explicitly stated, otherwise null.
             */
  name?: string | null;
  /**
            The BEST (at MOST 2 handles per type) contact methods for the adjuster (phone, email, fax) if explicitly stated,
            otherwise null.
             */
  handles?: ContactHandleSchema[] | null;
  /**
            Metadata about the source of this adjuster data (BPO, user edit, system, etc.).
            None indicates unknown/legacy data for backwards compatibility.
             */
  source?: CoverageDataSourceInfo;
};
export type ClaimDetail = {
  /**
            The benefit against which the claim is being made, if explicitly stated, otherwise null.
            
            <benefits-type-glossary>
                PIP == PERSONAL_INJURY_PROTECTION
                MEDPAY == MEDICAL_PAYMENTS
                UM == UNINSURED_MOTORIST
                UIM == UNDER_INSURED_MOTORIST
                BI == BODILY_INJURY_LIABILITY
                COMPREHENSIVE == COMPREHENSIVE_AUTO
                COLLISION == COLLISION_AUTO
                anything else == OTHER
            </benefits-type-glossary>
              
             */
  benefit_type?: BenefitType;
  /**
            The claim identifier (number, id, etc.) for the policy, if explicitly stated, otherwise null.
             */
  claim_identifier?: string | null;
  /**
            Metadata about the source of this claim data (BPO, user edit, system, etc.).
            None indicates unknown/legacy data for backwards compatibility.
             */
  source?: CoverageDataSourceInfo;
};
export type VehicleDetail = {
  /**
            The make of the vehicle if explicitly stated, otherwise null.
             */
  make?: string | null;
  /**
            The model of the vehicle if explicitly stated, otherwise null.
             */
  model?: string | null;
  /**
            The year of the vehicle if explicitly stated, otherwise null.
             */
  year?: number | null;
  /**
            The VIN of the vehicle if explicitly stated, otherwise null.
             */
  vin?: string | null;
  /**
            The license plate of the vehicle if explicitly stated, otherwise null.
             */
  license_plate?: string | null;
  /**
            The color of the vehicle if explicitly stated, otherwise null.
             */
  color?: string | null;
};
export type PolicyDetail = {
  /**
            A well formatted label for the policy detail, i.e "VIN", "Vehicle Description", "Premium", "Insured Address", "Garage Address"
             */
  key: string;
  /**
            The value of the policy detail, i.e "1234567890", "Toyota Camry", "100000.00"
             */
  value: string;
};
export type SubrogatorDetail = {
  /**
            Name of the subrogator handling a related claim if explicitly stated, otherwise null.
             */
  name?: string | null;
  /**
            The BEST (at MOST 2 handles per type) contact methods for the subrogator (phone, email, fax) if explicitly stated,
            otherwise null.
             */
  handles?: ContactHandleSchema[] | null;
  /**
            List of sources that provided the subrogator information if explicitly stated, otherwise null.
             */
  sources?: WebSource[] | null;
};
export type PropertyContactType =
  | "PROPERTY_OWNER"
  | "PROPERTY_MANAGER"
  | "OTHER";
export type PropertyContact = {
  /** The type of contact (owner, manager, or other). */
  contact_type: PropertyContactType;
  /** Custom label when contact_type is OTHER (e.g., 'Tenant', 'Security Company'). */
  custom_type_label?: string | null;
  /** Name of the contact person or entity. */
  name?: string | null;
  /** Contact methods: phone, email, fax, etc. */
  handles?: ContactHandleSchema[] | null;
  /** Mailing address for the contact. */
  address?: string | null;
  /** Additional notes about this contact. */
  notes?: string | null;
};
export type CoverageStatus =
  | "NEEDS_APPROVAL"
  | "APPROVED"
  | "REJECTED"
  | "NOT_APPLICABLE"
  | "DUPLICATE";
export type CoverageLiabilityStatus =
  | "ACCEPTED"
  | "REJECTED"
  | "PARTIALLY_ACCEPTED"
  | "LIABILITY_UNKNOWN"
  | "UNDER_INVESTIGATION";
export type ArtifactType =
  | "TRANSCRIPT"
  | "AUDIO_CONVERSATION"
  | "DOCUMENT"
  | "EMAIL"
  | "EMAIL_ATTACHMENT"
  | "INTAKE_NOTE"
  | "MESSAGE"
  | "MESSAGE_ATTACHMENT"
  | "CONVERSATION"
  | "BLOB"
  | "STAFF_NOTE";
export type CoverageMentionStubSchema = {
  id: string | string;
  created_at: string;
  artifact_id: string | string;
  artifact_type: ArtifactType;
  citation_ids?: string[] | null;
  confidence?: number | null;
  context?: string | null;
};
export type EnrichmentSource =
  | "perplexity"
  | "cached"
  | "manual"
  | "intake"
  | "extraction";
export type EnrichmentMetadata = {
  source_matter_id?: string | null;
  source_matter_name?: string | null;
  match_reason?: string | null;
  cached_at?: string | null;
  confidence_score?: number | null;
  [key: string]: any;
};
export type LorUploadedArtifactSummary = {
  id: string | string;
  blob_id?: (string | string) | null;
  name?: string | null;
  original_filename?: string | null;
  file_extension?: string | null;
  file_mime_type?: string | null;
  created_at?: string | null;
};
export type OpenClaimFieldStatus =
  | "VERIFIED"
  | "SUGGESTED"
  | "MISSING"
  | "UNKNOWN"
  | "WRONG";
export type OpenClaimCitedFieldStr = {
  /** The discovered or existing value for this field. */
  value?: string | null;
  /** The verification status of this field. */
  status?: OpenClaimFieldStatus;
  /** Confidence score (0-1) for the accuracy of this value. */
  confidence?: number | null;
  /** Citation IDs linking to source text in documents. */
  citation_ids?: string[];
  /** Artifact ID of the primary source document. */
  source_artifact_id?: (string | string) | null;
  /** Brief explanation of why this value was suggested or verified. */
  reasoning?: string | null;
};
export type OpenClaimClientInfo = {
  /** Client's full legal name. */
  name?: OpenClaimCitedFieldStr;
  /** Client's contact phone number. */
  phone?: OpenClaimCitedFieldStr;
  /** Client's email address. */
  email?: OpenClaimCitedFieldStr;
  /** Client's current mailing address (street address). */
  address?: OpenClaimCitedFieldStr;
  /** Client's city. */
  city?: OpenClaimCitedFieldStr;
  /** Client's ZIP code. */
  zip_code?: OpenClaimCitedFieldStr;
  /** Client's date of birth (MM/DD/YYYY). */
  dob?: OpenClaimCitedFieldStr;
  /** Last four digits of client's Social Security Number. */
  last_four_ssn?: OpenClaimCitedFieldStr;
};
export type OpenClaimIncidentInfo = {
  /** Date the incident occurred (MM/DD/YYYY). */
  date_of_loss?: OpenClaimCitedFieldStr;
  /** Time the incident occurred (e.g., 2:30 PM or 14:30). */
  time_of_loss?: OpenClaimCitedFieldStr;
  /** Location where the incident occurred (street address or intersection). */
  location?: OpenClaimCitedFieldStr;
  /** Weather conditions at time of incident. */
  weather_conditions?: OpenClaimCitedFieldStr;
  /** Brief description of how the incident occurred. */
  incident_description?: OpenClaimCitedFieldStr;
};
export type OpenClaimCitedFieldUnionIntNoneType = {
  /** The discovered or existing value for this field. */
  value?: number | null;
  /** The verification status of this field. */
  status?: OpenClaimFieldStatus;
  /** Confidence score (0-1) for the accuracy of this value. */
  confidence?: number | null;
  /** Citation IDs linking to source text in documents. */
  citation_ids?: string[];
  /** Artifact ID of the primary source document. */
  source_artifact_id?: (string | string) | null;
  /** Brief explanation of why this value was suggested or verified. */
  reasoning?: string | null;
};
export type OpenClaimVehicleInfo = {
  /** Vehicle model year. */
  year?: OpenClaimCitedFieldUnionIntNoneType;
  /** Vehicle manufacturer (e.g., Toyota, Ford). */
  make?: OpenClaimCitedFieldStr;
  /** Vehicle model name (e.g., Camry, F-150). */
  model?: OpenClaimCitedFieldStr;
  /** Vehicle Identification Number. */
  vin?: OpenClaimCitedFieldStr;
  /** License plate number and state. */
  license_plate?: OpenClaimCitedFieldStr;
  /** Vehicle color. */
  color?: OpenClaimCitedFieldStr;
  /** UUID of the coverage this vehicle should be assigned to. */
  suggested_coverage_id?: OpenClaimCitedFieldStr;
};
export type OpenClaimCoverageInfo = {
  /** Name of the insurance carrier. */
  carrier_name?: OpenClaimCitedFieldStr;
  /** Policy number on the insurance card/documents. */
  policy_number?: OpenClaimCitedFieldStr;
  /** Claims phone number for the carrier. */
  carrier_phone?: OpenClaimCitedFieldStr;
  /** Name of the policyholder/named insured. */
  named_insured?: OpenClaimCitedFieldStr;
  /** Existing claim number if a claim has already been filed. */
  claim_number?: OpenClaimCitedFieldStr;
};
export type OpenClaimAtFaultDriverInfo = {
  /** Name of the at-fault or other involved driver. */
  name?: OpenClaimCitedFieldStr;
  /** Phone number of the at-fault driver. */
  phone?: OpenClaimCitedFieldStr;
  /** Address of the at-fault driver. */
  address?: OpenClaimCitedFieldStr;
};
export type OpenClaimContextSchema = {
  /** Client/claimant information. */
  client?: OpenClaimClientInfo;
  /** Details about the incident/accident. */
  incident?: OpenClaimIncidentInfo;
  /** Client's vehicle(s) involved in the incident. */
  client_vehicles?: OpenClaimVehicleInfo[];
  /** Insurance coverage details. */
  coverage?: OpenClaimCoverageInfo;
  /** At-fault or other party driver information. */
  at_fault_driver?: OpenClaimAtFaultDriverInfo;
  /** At-fault or other party vehicle information. */
  defendant_vehicle?: OpenClaimVehicleInfo;
  /** ISO timestamp when AI discovery completed. */
  discovery_completed_at?: string | null;
  /** Summary of what was found and what's missing. */
  discovery_summary?: string | null;
};
export type CoverageSchema = {
  /**
            The type of policy if explicitly stated, otherwise null.
             */
  policy_type?: PolicyType;
  /**
            Details about the insurance carrier if mentioned, otherwise null.
             */
  carrier?: CarrierDetail;
  /**
            Carrier-issued identifiers for the policy, if explicitly stated, otherwise null.
             */
  policy_identifiers?: PolicyIdentifier[] | null;
  /**
            List of names of primary policyholders or insured entities if stated or implied, otherwise null.
             */
  named_insured?: string[] | null;
  /**
            List of benefits covered by the policy, if explicitly stated, otherwise null.
             */
  benefits?: BenefitDetail[] | null;
  /**
            Any known adjusters handling claims against this policy, if explicitly stated, otherwise null.
             */
  adjusters?: AdjusterDetail[] | null;
  /**
            Any known claims opened against the policy, if explicitly stated, otherwise null.
             */
  claims?: ClaimDetail[] | null;
  /**
            List of vehicles covered by the policy, if applicable and explicitly stated, otherwise null.
             */
  vehicles?: VehicleDetail[] | null;
  /**
            Structured list of extracted policy details, such as, but not limited to:
            VIN; vehicle description; premium amount; insured's address;
            garage location; policy endorsements; coverage exclusions;
            other policy-specific information for claims processing.
             */
  policy_details?: PolicyDetail[] | null;
  /**
            Details about the subrogator if health insurance policy + carrier ... if explicitly stated, otherwise null.
             */
  subrogator?: SubrogatorDetail;
  /**
            DEPRECATED: Use Property.owners instead. Owner/manager information is now stored via
            Organization parties linked to the Property subject through Ownership records.
            This field is retained for backward compatibility with existing data but is no longer
            populated for new coverages.
             */
  property_contacts?: PropertyContact[] | null;
  id: string | string;
  status?: CoverageStatus;
  liability_status?: CoverageLiabilityStatus;
  category?: CoverageCategory;
  matter_id: string | string;
  created_at: string;
  modified_at: string;
  /**
            List of mentions of this coverage in the matter, if any.
             */
  mentions?: CoverageMentionStubSchema[] | null;
  carrier_v2_id?: (string | string) | null;
  carrier_division_id?: (string | string) | null;
  /** Source of enrichment data for carrier information */
  enrichment_source?: EnrichmentSource;
  /** Metadata about enrichment source (source matter ID, match reason, etc.) */
  enrichment_metadata?: EnrichmentMetadata;
  /** Whether cached data was used for enrichment */
  cache_used?: boolean;
  lor_document_id?: (string | string) | null;
  lor_uploaded_artifact_id?: (string | string) | null;
  lor_uploaded_artifact?: LorUploadedArtifactSummary;
  /** AI-discovered context for opening a claim with citations and confidence scores. */
  open_claim_context?: OpenClaimContextSchema;
  created_by_user_id?: (string | string) | null;
  created_by_user_name?: string | null;
  /** For PROPERTY category: UUID of the linked Property subject. */
  property_id?: (string | string) | null;
  /** Whether this is the primary coverage within its party (1P or 3P). */
  is_primary?: boolean;
};
export type CoverageEntitySnapshot = {
  entity_type?: "COVERAGE";
  data: CoverageSchema;
};
export type BlobSchema = {
  id: string | string;
  name: string;
  original_filename: string | null;
  collection_id: string | string;
  collection_name: string;
  file_hash: string;
  file_size: number;
  file_extension: string | null;
  file_mime_type: string | null;
  download_url?: string | null;
  created_at: string;
  modified_at: string;
};
export type DocumentEntitySnapshot = {
  entity_type?: "DOCUMENT";
  data: BlobSchema;
};
export type ProviderStatus = "NEEDS_APPROVAL" | "APPROVED" | "REJECTED";
export type ProviderTreatmentStatus =
  | "TREATING"
  | "COMPLETED"
  | "DEVIATING"
  | "UNKNOWN"
  | "REFERRED_TO";
export type ProviderType = "ORGANIZATION" | "PRACTITIONER";
export type ProviderSearchResult = {
  /** Name of the practitioner (e.g. 'Dr. John Smith') if explicitly stated, null otherwise. */
  practitioner_name?: string | null;
  /** Common, recognizable name of the healthcare organization (e.g., 'UC San Diego Health', 'Main Street Clinic'). EXCLUDE administrative department names (e.g., 'Medical Records', 'Billing'); these belong in handles. Align with original mention's generality unless search strongly indicates a specific, distinct facility. */
  organization_name?: string | null;
  /** The address of the provider. */
  address?: string | null;
  /** The BEST (at MOST 2 handles per type) contact methods for records/billing retrieval. Do not include scheduling phone numbers, or other patient care oriented phone numbers. */
  handles: ContactHandleSchema[];
  /** A very brief (1-2 sentences) note for paralegal context. */
  internal_note?: string | null;
};
export type EncounterSchema = {
  /** The unique identifier for the encounter */
  id: string;
  /** The date of service for the encounter in ISO format (YYYY-MM-DD), if explicitly stated, null otherwise */
  date?: string | null;
  /** list of short phrases describing care provided in the encounter as mentioned in the artifact, like 'physical therapy', 'labs', 'imaging', etc. if explicitly stated, null otherwise */
  services?: string[];
  /** A very concise description of the encounter, as a one liner that might be used in a medical chronology for the case. Don't repeat the date of service in the description . Explicitly mention any ambiguities about the discovery that merit further investigation (i.e date of service not yet known, etc.) */
  description: string;
};
export type TreatmentFrequency =
  | "DAILY"
  | "WEEKLY"
  | "BI_WEEKLY"
  | "MONTHLY"
  | "AS_NEEDED"
  | "OTHER";
export type TreatmentProgress = "IMPROVING" | "STAGNANT" | "REGRESSING";
export type TreatmentCareLevel =
  | "EMERGENCY_CARE"
  | "CONSERVATIVE_CARE"
  | "DIAGNOSTIC_TESTING"
  | "SPECIALIST_CARE"
  | "SURGICAL_CARE";
export type InferredFieldSource = "AI" | "MANUAL";
export type ProcedureType =
  | "ER_VISIT"
  | "URGENT_CARE"
  | "CHIROPRACTIC_VISIT"
  | "PT_VISIT"
  | "MRI"
  | "CT_SCAN"
  | "XRAY"
  | "EMG_NCV"
  | "PAIN_MANAGEMENT_CONSULT"
  | "EPIDURAL_INJECTION"
  | "NERVE_BLOCK"
  | "SURGERY_RECOMMENDED"
  | "SURGERY_PERFORMED"
  | "ORTHO_CONSULT"
  | "FACET_INJECTION"
  | "TRIGGER_POINT_INJECTION"
  | "NEURO_CONSULT"
  | "NEUROPSYCH_EVAL"
  | "OTHER"
  | "UNKNOWN";
export type MedicalProviderSchema = {
  id: string;
  matter_id: string;
  provider_contact_ref_id?: string | null;
  provider_service_ref_id?: string | null;
  name: string;
  status?: ProviderStatus;
  dates_of_service?: string | null;
  treatment?: string | null;
  address?: string | null;
  phone?: string | null;
  fax?: string | null;
  email?: string | null;
  still_treating?: string | null;
  treatment_status?: ProviderTreatmentStatus;
  treatment_status_occurred_at?: string | null;
  date_of_records_request?: string | null;
  provider_type?: ProviderType;
  practitioner_names?: string[] | null;
  description?: string | null;
  handles: ContactHandleSchema[];
  inference_reasoning?: string | null;
  fallback_leads?: ProviderSearchResult[] | null;
  encounters?: EncounterSchema[] | null;
  pain_level?: number | null;
  treatment_notes?: string | null;
  treatment_frequency?: TreatmentFrequency;
  treatment_progress?: TreatmentProgress;
  care_level?: TreatmentCareLevel;
  /** Confidence (0-1) for care_level when set by AI. */
  care_level_confidence?: number | null;
  /** Whether care_level was set by AI or manually by a user. */
  care_level_source?: InferredFieldSource;
  /** When care_level was last set/updated. */
  care_level_updated_at?: string | null;
  /** Procedure types inferred for this provider. */
  procedure_types?: ProcedureType[] | null;
  /** Confidence (0-1) for procedure_types when set by AI. */
  procedure_types_confidence?: number | null;
  /** Whether procedure_types was set by AI or manually by a user. */
  procedure_types_source?: InferredFieldSource;
  /** When procedure_types was last set/updated. */
  procedure_types_updated_at?: string | null;
};
export type ProviderEntitySnapshot = {
  entity_type?: "PROVIDER";
  data: MedicalProviderSchema;
};
export type StaffNoteSchema = {
  content: string;
  matter_id: string | string;
  id: string | string;
  user_id: string | string;
  created_at: string;
  modified_at: string;
  agent_tags?: string[];
};
export type StaffNoteEntitySnapshot = {
  entity_type?: "STAFF_NOTE";
  data: StaffNoteSchema;
};
export type TaskGroups =
  | "Intake"
  | "Initial Review"
  | "Police Report & Investigation"
  | "1P Insurance"
  | "3P Insurance"
  | "Medical Records"
  | "Treatment"
  | "Demand Letter"
  | "Liens"
  | "Miscellaneous"
  | "Additional Insurance"
  | "Initial Medical Records";
export type TaskType =
  | "Medical Records"
  | "Medical Bills"
  | "LOR Acknowledgement"
  | "Send LOR"
  | "Open Claim"
  | "Final Lien"
  | "Lien Acknowledgement"
  | "Police Report"
  | "Police Report Escalation"
  | "Freedom of Information Act"
  | "Incident Report"
  | "Open File"
  | "Find Health Insurance Coverage"
  | "Locate Report Source"
  | "Ascertain Subrogator"
  | "Finalize Demand"
  | "Draft Demand"
  | "Demand Readiness Review"
  | "Declaration Page"
  | "Health Insurance Card"
  | "Treatment Check-In"
  | "Treatment Barrier"
  | "HIPAA Directive"
  | "Retainer Agreement"
  | "Conduct Intake"
  | "Conduct Initial Review"
  | "Welcome Call"
  | "Other"
  | "All"
  | "Review Found Coverage"
  | "Review Found Provider"
  | "Update External CMS"
  | "Find 1P Coverage"
  | "Find 3P Coverage"
  | "Client Communications"
  | "Managing Paralegal Asks"
  | "Manage MedPay"
  | "Send MedPay Letter"
  | "Get Liability Limits"
  | "Request Updated PIP Ledger"
  | "Send Evidence Preservation Letter"
  | "Document Collection"
  | "Review 1P Benefits"
  | "PIP/MedPay Application"
  | "Check for PIP Ledger"
  | "IME Required"
  | "UM/UIM Indicate Stack vs. No Stack"
  | "Generate No Medpay Letter"
  | "Provide PIP Info to Medical Provider"
  | "Liability Other"
  | "1P Insurance Other"
  | "3P Insurance Other"
  | "Liens Other"
  | "Treatment Other"
  | "Medical Records Other"
  | "Demand Letter Other"
  | "Confirm Incident Location"
  | "Research Property & Owners";
export type TaskExternalCmsUpdateStatus = "NOT_UPDATED" | "UPDATED";
export type TaskTemplateType =
  | "intake_conduct_intake"
  | "intake_conduct_initial_review"
  | "intake_signed_retainer"
  | "intake_signed_hipaa"
  | "intake_health_insurance_card"
  | "intake_declaration_page"
  | "intake_update_external_cms"
  | "intake_welcome_call"
  | "initial_review_client_drivers_license"
  | "initial_review_defendant_drivers_license"
  | "initial_review_client_auto_insurance"
  | "initial_review_request_supplemental"
  | "police_locate_police_report"
  | "police_retrieve_police_report"
  | "police_confirm_found_report"
  | "police_eliminated_pd"
  | "police_provide_missing_info"
  | "police_not_applicable"
  | "police_researching"
  | "police_unverified"
  | "police_verified"
  | "police_manual_intervention_required"
  | "police_confirm_no_report_exists"
  | "police_report_v2"
  | "police_foia_request"
  | "police_incident_report"
  | "insurance_1p_open_1p_claim"
  | "insurance_1p_send_1p_lor"
  | "insurance_1p_receive_1p_acknowledgement"
  | "insurance_1p_get_liability_limits"
  | "insurance_1p_declaration_page"
  | "insurance_3p_open_3p_claim"
  | "insurance_3p_send_3p_lor"
  | "insurance_3p_receive_3p_acknowledgement"
  | "insurance_3p_get_liability_limits"
  | "liens_open_file"
  | "liens_final_lien"
  | "liens_receive_lien_acknowledgement"
  | "liens_find_health_insurance_coverage"
  | "demands_draft_demand"
  | "demands_finalize_demand"
  | "demands_demand_readiness_review"
  | "treatment_treatment_check_in"
  | "treatment_resolve_barrier"
  | "treatment_requests_submit_request"
  | "medical_provider_review_found_provider"
  | "medical_provider_request_medical_records"
  | "treatment_record_request"
  | "medical_provider_request_medical_bills"
  | "medical_provider_request_affidavit"
  | "coverage_review_found_coverage"
  | "coverage_find_1p_coverage"
  | "coverage_find_3p_coverage"
  | "coverage_review_1p_benefits"
  | "coverage_pip_medpay_application"
  | "coverage_check_for_pip_ledger"
  | "coverage_ime_required"
  | "coverage_generate_no_medpay_letter"
  | "coverage_manage_medpay"
  | "coverage_provide_pip_info_to_provider"
  | "premises_confirm_incident_location"
  | "premises_research_property_owners"
  | "premises_send_lor"
  | "escalation_case_escalation";
export type TaskSchema = {
  id: string | string;
  matter_id?: (string | string) | null;
  name: string;
  description: string;
  group: TaskGroups;
  type: TaskType;
  due_date?: string | null;
  medical_note_id?: (string | string) | null;
  coverage_id?: (string | string) | null;
  police_report_id?: (string | string) | null;
  treatment_record_request_id?: (string | string) | null;
  external_cms_update_status?: TaskExternalCmsUpdateStatus;
  external_cms_update_reason?: string | null;
  assignee_id?: (string | string) | null;
  created_by_id?: (string | string) | null;
  template_type?: TaskTemplateType;
};
export type TaskEntitySnapshot = {
  entity_type?: "TASK";
  data: TaskSchema;
};
export type TaskUpdateType = "comment" | "activity";
export type TaskUpdateVisibility = "Private" | "Public";
export type CustomUserSchema = {
  id: string | string;
  username: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  is_firm_admin?: boolean;
  avatar?: string | null;
  signature?: string | null;
};
export type AgentType =
  | "ingest_transcript_task"
  | "provider_finder"
  | "artifact_digest"
  | "treatment_researcher"
  | "treatment_request_automation"
  | "bland_caller"
  | "voice_ai"
  | "slack_task_bot"
  | "blob_sorter"
  | "front_app_integration"
  | "task_generator"
  | "coverage_extractor"
  | "intake_converter"
  | "airtable_sync"
  | "system"
  | "backfill_script"
  | "domain_event_update_orchestrator"
  | "loa_detector"
  | "rainmaker"
  | "deliverable_sync"
  | "bpo_agent"
  | "loa_chaser"
  | "external_task_rule_engine"
  | "external_task_bulk_create"
  | "entity_sync"
  | "case_activation";
export type TaskUpdateSchema = {
  id: string | string;
  task_id: string | string;
  update: string;
  update_type: TaskUpdateType;
  visibility: TaskUpdateVisibility;
  user?: CustomUserSchema;
  agent?: AgentType;
  agent_tags?: string[];
  correlation_id?: (string | string) | null;
  created_at: string;
  modified_at: string;
};
export type TaskUpdateEntitySnapshot = {
  entity_type?: "TASK_UPDATE";
  data: TaskUpdateSchema;
};
export type TreatmentRecordRequestActor = "user" | "agent";
export type TreatmentRecordTypes =
  | "Bills"
  | "Records"
  | "Facility Records"
  | "Facility Bills"
  | "Radiology"
  | "ER Physician Bills"
  | "Other"
  | "Unknown";
export type TreatmentRecordRequestStatus =
  | "not_ready"
  | "pending"
  | "request_submitted"
  | "request_received"
  | "awaiting_invoice"
  | "pending_payment"
  | "records_pending"
  | "records_delivered"
  | "records_ingested"
  | "rejected"
  | "cancelled"
  | "auto_retried";
export type Blockers =
  | "HIPAA_PACKAGE_NOT_READY"
  | "CLIENT_INFORMATION_NOT_ENTERED"
  | "PROVIDER_CONTACT_INFORMATION_NOT_ENTERED"
  | "PORTAL_REQUEST_ID_REQUIRED"
  | "NO_REQUEST_SENT_AT"
  | "VOICE_AI_INFORMATION_NOT_ENTERED"
  | "FOLLOWUP_HIPAA_PACKAGE_NOT_SUBMITTED"
  | "FOLLOWUP_CHANNEL_NOT_CONFIGURED"
  | "FOLLOWUP_CHANNEL_UNSUPPORTED"
  | "FOLLOWUP_FAX_NUMBER_MISSING"
  | "FOLLOWUP_EMAIL_ADDRESS_MISSING"
  | "FOLLOWUP_TASK_TERMINAL";
export type TreatmentRecordRequestAutomationBlockedSchema = {
  details?: string | null;
  checked_status_at?: string | null;
  status?: "blocked";
  blocked_by?: Blockers[];
};
export type TreatmentRecordRequestAutomationFailedSchema = {
  details?: string | null;
  checked_status_at?: string | null;
  status?: "failed";
};
export type TreatmentRecordRequestAutomationIdleSchema = {
  details?: string | null;
  checked_status_at?: string | null;
  status?: "idle";
};
export type TreatmentRecordRequestAutomationRunningSchema = {
  details?: string | null;
  checked_status_at?: string | null;
  status?: "running";
};
export type TreatmentRecordRequestAutomationSleepingSchema = {
  details?: string | null;
  checked_status_at?: string | null;
  status?: "sleeping";
  sleep_until: string;
};
export type PatientInformation = {
  client_name: string;
  client_date_of_birth: string;
};
export type TreatmentRecordRequestChannelType =
  | "portal"
  | "human"
  | "email"
  | "fax"
  | "mail"
  | "phone";
export type RequestDetails = {
  request_method: TreatmentRecordRequestChannelType;
  request_sent_at: string;
  request_method_target: string;
  contact_number: string;
};
export type RequesteeInformation = {
  requestee_name: string;
};
export type VoiceAiCallArgs = {
  patient_information: PatientInformation;
  request_details: RequestDetails;
  requestee_information: RequesteeInformation;
  /** Human-readable description of the requested records (e.g., Medical Records, Bills, Radiology). */
  request_type_label?: string | null;
  /** Skip business-hours scheduling and execute the call immediately. Used for manual/bulk calls that should run ASAP. */
  skip_scheduling_delay?: boolean;
};
export type MrFollowupObservationSchemaVersion = "mr_followup_observation_v1";
export type MrFollowupConnectionOutcome =
  | "HUMAN"
  | "VOICEMAIL"
  | "IVR"
  | "NO_ANSWER"
  | "BUSY"
  | "INVALID_NUMBER"
  | "FAILED"
  | "UNKNOWN";
export type MrFollowupEtaUnit =
  | "HOURS"
  | "DAYS"
  | "WEEKS"
  | "BUSINESS_DAYS"
  | "BUSINESS_HOURS";
export type MrFollowupConnectionFollowupInstructions = {
  details?: string | null;
  eta_value?: number | null;
  eta_unit?: MrFollowupEtaUnit;
  phone?: string | null;
};
export type MrFollowupConnection = {
  outcome: MrFollowupConnectionOutcome;
  attempts_before_connect?: number | null;
  voicemail_left?: boolean | null;
  followup_instructions?: MrFollowupConnectionFollowupInstructions;
};
export type MrFollowupContactOutcome =
  | "WRONG_NUMBER_REDIRECT"
  | "WRONG_PROVIDER"
  | "INFO_INSUFFICIENT"
  | "CALLBACK_REQUESTED"
  | "NOT_FOUND_REDIRECT"
  | "NOT_FOUND_RETRY"
  | "REQUEST_FOUND"
  | "UNKNOWN";
export type MrFollowupRedirectMethod =
  | "FAX"
  | "EMAIL"
  | "PORTAL"
  | "MAIL"
  | "OTHER";
export type MrFollowupContactRedirect = {
  new_phone?: string | null;
  new_destination?: string | null;
  new_destination_method?: MrFollowupRedirectMethod;
};
export type MrFollowupContactCallback = {
  callback_time?: string | null;
  reason?: string | null;
  callback_phone?: string | null;
};
export type MrFollowupContactInfoInsufficient = {
  info_needed?: string | null;
};
export type MrFollowupContact = {
  outcome: MrFollowupContactOutcome;
  department_reached?: string | null;
  representative_name?: string | null;
  redirect?: MrFollowupContactRedirect;
  callback?: MrFollowupContactCallback;
  info_insufficient?: MrFollowupContactInfoInsufficient;
};
export type MrFollowupStatusOutcome =
  | "IN_PROCESSING"
  | "BLOCKED_LETTERHEAD"
  | "BLOCKED_AUTH"
  | "BLOCKED_DOCS"
  | "REJECTED_NOT_PATIENT"
  | "REJECTED_NO_RECORDS"
  | "REJECTED_AFTER_PROCESSING"
  | "RECORDS_READY"
  | "UNKNOWN";
export type MrFollowupInProcessing = {
  eta_value?: number | null;
  eta_unit?: MrFollowupEtaUnit;
  followup_contact_name?: string | null;
  followup_phone?: string | null;
  reference_number?: string | null;
  additional_reference_numbers?: string[] | null;
};
export type MrFollowupBlocked = {
  details?: string | null;
};
export type MrFollowupRejected = {
  details?: string | null;
  representative_name?: string | null;
};
export type MrFollowupStatus = {
  outcome: MrFollowupStatusOutcome;
  in_processing?: MrFollowupInProcessing;
  blocked?: MrFollowupBlocked;
  rejected?: MrFollowupRejected;
};
export type MrFollowupInvoiceOutcome =
  | "NO_INVOICE"
  | "INVOICE_SENT"
  | "INVOICE_PENDING"
  | "INVOICE_UNKNOWN"
  | "UNKNOWN";
export type MrFollowupInvoiceChannel =
  | "FAX"
  | "EMAIL"
  | "MAIL"
  | "PORTAL"
  | "OTHER";
export type MrFollowupInvoice = {
  outcome: MrFollowupInvoiceOutcome;
  amount?: number | null;
  channel?: MrFollowupInvoiceChannel;
  destination?: string | null;
  date_sent?: string | null;
  eta?: string | null;
  invoice_number?: string | null;
};
export type MrFollowupPaymentOutcome =
  | "PAYMENT_ON_CALL"
  | "PAYMENT_OFFLINE_MAIL"
  | "PAYMENT_OFFLINE_ONLINE"
  | "PAYMENT_PENDING"
  | "PAYMENT_CONFIRMED"
  | "UNKNOWN";
export type MrFollowupPayment = {
  outcome?: MrFollowupPaymentOutcome;
  amount_paid?: number | null;
  confirmation_number?: string | null;
  payee_name?: string | null;
  mailing_address?: string | null;
  portal_url?: string | null;
  account_instructions?: string | null;
  date_paid?: string | null;
};
export type MrFollowupDeliveryOutcome =
  | "RECORDS_SENT"
  | "RECORDS_PENDING_DELIVERY"
  | "RECORDS_READY_PICKUP"
  | "UNKNOWN";
export type MrFollowupDeliveryMethod =
  | "FAX"
  | "EMAIL"
  | "PORTAL"
  | "MAIL"
  | "COURIER"
  | "PICKUP"
  | "OTHER";
export type MrFollowupDelivery = {
  outcome?: MrFollowupDeliveryOutcome;
  method?: MrFollowupDeliveryMethod;
  destination?: string | null;
  date_sent?: string | null;
  eta?: string | null;
  portal_url?: string | null;
  access_instructions?: string | null;
  pickup_location?: string | null;
  special_instructions?: string | null;
};
export type AlternateContactHandleType = "fax" | "email" | "mailing_address";
export type AlternateContactSchema = {
  /** Type of contact method (fax/email/mailing address). */
  handle_type: AlternateContactHandleType;
  /** The actual contact value (fax number, email, or mailing address). */
  value: string;
  /** Optional human-readable label or description. */
  label?: string | null;
  /** Optional confidence score from AI extraction (0-1). */
  confidence?: number | null;
};
export type MrFollowupEvidenceEntry = {
  field: string;
  quote: string;
};
export type MrFollowupObservationV1 = {
  schema_version: MrFollowupObservationSchemaVersion;
  summary: string;
  related_reference_numbers?: string[] | null;
  connection: MrFollowupConnection;
  contact?: MrFollowupContact;
  status?: MrFollowupStatus;
  invoice?: MrFollowupInvoice;
  payment?: MrFollowupPayment;
  delivery?: MrFollowupDelivery;
  alternate_contacts?: AlternateContactSchema[];
  evidence: MrFollowupEvidenceEntry[];
};
export type MrFollowupDecisionSchemaVersion = "mr_followup_decision_v1";
export type MrFollowupNextActor = "SYSTEM" | "USER" | "BPO" | "STOP";
export type MrFollowupNextAction =
  | "RETRY_CALL"
  | "WAIT_FOR_CALLBACK"
  | "CALL_BACK_AT_TIME"
  | "CALL_NEW_NUMBER"
  | "RESEARCH_NEW_NUMBER"
  | "RESUBMIT_SAME_DESTINATION"
  | "RESUBMIT_NEW_DESTINATION"
  | "PROVIDE_MISSING_INFO"
  | "PROVIDE_MISSING_DOCS"
  | "VERIFY_PATIENT_INFO"
  | "WAIT_FOR_PROCESSING"
  | "WAIT_FOR_INVOICE"
  | "PAY_INVOICE"
  | "WAIT_FOR_PAYMENT_CONFIRMATION"
  | "CHECK_INBOX_FOR_RECORDS"
  | "WAIT_FOR_RECORDS_DELIVERY"
  | "RETRIEVE_RECORDS"
  | "ESCALATE_TO_BPO"
  | "STOP_NO_ACTION"
  | "STOP_REJECTED";
export type MrFollowupDecisionActionContext = {
  notes?: string | null;
  callback_time?: string | null;
  new_phone?: string | null;
  new_destination?: string | null;
  followup_eta_value?: number | null;
  followup_eta_unit?: MrFollowupEtaUnit;
};
export type MrFollowupDecisionV1 = {
  schema_version: MrFollowupDecisionSchemaVersion;
  next_actor: MrFollowupNextActor;
  next_action: MrFollowupNextAction;
  next_action_at?: string | null;
  required_user_inputs: string[];
  rationale: string;
  action_context?: MrFollowupDecisionActionContext;
};
export type MrFollowupQualitySchemaVersion = "mr_followup_quality_v1";
export type MrFollowupQualityFailureMode =
  | "SCHEMA_VIOLATION"
  | "UNGROUNDED_CLAIMS"
  | "MISCLASSIFIED_OUTCOME"
  | "MISSING_KEY_PRIMITIVE"
  | "BAD_EVIDENCE"
  | "BAD_NEXT_ACTION"
  | "OTHER";
export type MrFollowupQualityV1 = {
  schema_version: MrFollowupQualitySchemaVersion;
  overall_score: number;
  should_trust: boolean;
  notes: string;
  grounding_score: number;
  decision_score: number;
  evidence_score: number;
  primary_failure_mode?: MrFollowupQualityFailureMode;
};
export type ContactDiscoveryCallSummarySchema = {
  /** Short text describing what the facility told us. */
  call_summary: string;
  /** List of submission channels (portal, fax, email) mentioned during the call. */
  submission_methods?: string[];
  /** Documents or prerequisites (HIPAA, ID, invoices) called out by the facility. */
  required_documents?: string[];
  /** Structured alternates (fax numbers, mailing addresses) discovered during the call. */
  alternate_contacts?: AlternateContactSchema[];
  /** Optional free-form notes for additional context. */
  notes?: string | null;
};
export type TransmissionStrategyConfig = {
  enabled?: boolean;
  channel?: TreatmentRecordRequestChannelType;
  interval_days?: number;
  max_attempts?: number;
  stop_on_voice_ai_contact?: boolean;
  stop_on_status_change?: boolean;
  last_transmission_at?: string | null;
  next_transmission_at?: string | null;
  total_transmissions_sent?: number;
  failed_attempts?: number;
};
export type TreatmentRecordRequestMetadataSchema = {
  slack_message_id?: string | null;
  disable_automations?: boolean;
  automation_state_v2?:
    | (
        | ({
            status: "blocked";
          } & TreatmentRecordRequestAutomationBlockedSchema)
        | ({
            status: "failed";
          } & TreatmentRecordRequestAutomationFailedSchema)
        | ({
            status: "idle";
          } & TreatmentRecordRequestAutomationIdleSchema)
        | ({
            status: "running";
          } & TreatmentRecordRequestAutomationRunningSchema)
        | ({
            status: "sleeping";
          } & TreatmentRecordRequestAutomationSleepingSchema)
      )
    | null;
  ad_hoc_voice_ai_args?: VoiceAiCallArgs;
  front_conversation_id?: string | null;
  front_conversation_url?: string | null;
  email_sent_at?: string | null;
  /** Number of days between voice AI follow-up calls */
  voice_ai_check_interval_days?: number;
  next_scheduled_call?: string | null;
  request_submitted_at?: string | null;
  initial_contact_method_call_completed_at?: string | null;
  voice_ai_call_summary?: string | null;
  voice_ai_discovered_alternates?: {
    [key: string]: any;
  }[];
  voice_ai_call_id?: string | null;
  superseded_by_request_id?: string | null;
  supersession_reason?: string | null;
  last_notification_category?: string | null;
  last_notification_subcategory?: string | null;
  last_call_key_information?: {
    [key: string]: any;
  };
  last_voice_ai_call_at?: string | null;
  last_voice_ai_call_summary?: string | null;
  mr_followup_vnext_call_id?: string | null;
  mr_followup_vnext_generated_at?: string | null;
  mr_followup_observation_v1?: MrFollowupObservationV1;
  mr_followup_decision_v1?: MrFollowupDecisionV1;
  mr_followup_quality_v1?: MrFollowupQualityV1;
  mr_followup_vnext_observation_used_audio?: boolean | null;
  mr_followup_vnext_observation_fallback_reason?: string | null;
  mr_followup_vnext_decision_used_audio?: boolean | null;
  mr_followup_vnext_decision_fallback_reason?: string | null;
  mr_followup_vnext_quality_used_audio?: boolean | null;
  mr_followup_vnext_quality_fallback_reason?: string | null;
  dynamic_retry_delay_hours?: number | null;
  contact_discovery_summary?: ContactDiscoveryCallSummarySchema;
  contact_discovery_last_updated_at?: string | null;
  hipaa_package_locked?: boolean;
  hipaa_package_locked_at?: string | null;
  hipaa_package_submission_channel?: TreatmentRecordRequestChannelType;
  hipaa_package_submission_workflow_id?: string | null;
  alternate_contact?: AlternateContactSchema;
  forked_from_request_id?: string | null;
  transmission_strategy?: TransmissionStrategyConfig;
};
export type TreatmentRecordRequestPortalType =
  | "smart_request"
  | "mro"
  | "chartswap";
export type PortalRecordType = {
  id: string | string;
  created_at: string;
  modified_at: string;
  original_name?: string | null;
  record_type: TreatmentRecordTypes;
};
export type PortalProvider = {
  id: string | string;
  created_at: string;
  modified_at: string;
  name: string;
  portal: TreatmentRecordRequestPortalType;
  active?: boolean;
  external_id?: string | null;
  site?: string | null;
  website?: string | null;
  phone?: string | null;
  email?: string | null;
  fax?: string | null;
  address_line_1?: string | null;
  address_line_2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  record_types?: PortalRecordType[] | null;
};
export type TreatmentRecordRequestChannelSchema = {
  id: string | string;
  created_at: string;
  modified_at: string;
  type: TreatmentRecordRequestChannelType;
  portal_provider_id?: (string | string) | null;
  phone_number?: string | null;
  address?: string | null;
  email?: string | null;
  fax?: string | null;
  instructions?: string | null;
  portal_record_request_id?: string | null;
  provider_contact_unit_handle_id?: (string | string) | null;
  provider_directory_contact_point_id?: (string | string) | null;
  portal_provider?: PortalProvider;
};
export type FaxTransmissionStatusSchema = {
  id?: (string | string) | null;
  status?: ("pending" | "sent" | "delivered" | "failed") | null;
  sent_at?: string | null;
  delivered_at?: string | null;
  failed_at?: string | null;
  transmission_id?: string | null;
  error_message?: string | null;
};
export type EmailTransmissionStatusSchema = {
  sent_at?: string | null;
  front_conversation_id?: string | null;
  front_conversation_url?: string | null;
  recipient_email?: string | null;
};
export type TreatmentRecordRequestSchema = {
  id: string | string;
  created_at: string;
  modified_at: string;
  provider_id: string | string;
  actor?: TreatmentRecordRequestActor;
  is_certified?: boolean;
  is_urgent?: boolean;
  provided_records?: TreatmentRecordTypes[];
  status?: TreatmentRecordRequestStatus;
  metadata?: TreatmentRecordRequestMetadataSchema;
  channel?: TreatmentRecordRequestChannelSchema;
  fax_transmission_status?: FaxTransmissionStatusSchema;
  email_transmission_status?: EmailTransmissionStatusSchema;
  task_id?: (string | string) | null;
};
export type TreatmentRequestEntitySnapshot = {
  entity_type?: "TREATMENT_REQUEST";
  data: TreatmentRecordRequestSchema;
};
export type EntityChangeStatus = "pending" | "transferred" | "skipped";
export type EntityChangeType = "ignored" | "created" | "updated" | "deleted";
export type PatchOperation = "replace" | "add" | "remove";
export type JsonValue =
  | (
      | string
      | number
      | number
      | boolean
      | JsonValue[]
      | {
          [key: string]: JsonValue;
        }
    )
  | null;
export type AugmentedPatchOp = {
  op: PatchOperation;
  path: string;
  new_value?: JsonValue;
  old_value?: JsonValue;
};
export type BlobSinkActionStatus =
  | "PENDING"
  | "PROCESSING"
  | "AWAITING_APPROVAL"
  | "WAITING_FOR_PUSH_WINDOW"
  | "READY_TO_PUSH"
  | "PUSHING"
  | "PUSHED"
  | "FAILED"
  | "ALREADY_EXISTS"
  | "REJECTED"
  | "REMOVED";
export type BlobSinkActionTargetSystem =
  | "FILEVINE"
  | "LITIFY"
  | "NEOS"
  | "CLIO"
  | "SMARTADVOCATE"
  | "DROPBOX"
  | "CASEPEER"
  | "MYCASE"
  | "GOOGLE_DRIVE";
export type BlobSinkApprovalMode = "AUTOMATIC" | "HUMAN";
export type BlobSinkApprovalStatus = "PENDING" | "APPROVED" | "REJECTED";
export type DocumentSyncInfo = {
  action_id: string;
  status: BlobSinkActionStatus;
  target_system: BlobSinkActionTargetSystem;
  target_path?: string | null;
  target_name?: string | null;
  approval_mode: BlobSinkApprovalMode;
  approval_status: BlobSinkApprovalStatus;
  error_message?: string | null;
  last_workflow_run_id?: string | null;
  created_at?: string | null;
  modified_at?: string | null;
};
export type EntityChangeSchema = {
  id: string | string;
  matter_id: string | string;
  entity_type: EntityCategory;
  entity_id: string;
  old_snapshot?:
    | (
        | ({
            entity_type: "CASE_EXPENSE";
          } & CaseExpenseEntitySnapshot)
        | ({
            entity_type: "COVERAGE";
          } & CoverageEntitySnapshot)
        | ({
            entity_type: "DOCUMENT";
          } & DocumentEntitySnapshot)
        | ({
            entity_type: "PROVIDER";
          } & ProviderEntitySnapshot)
        | ({
            entity_type: "STAFF_NOTE";
          } & StaffNoteEntitySnapshot)
        | ({
            entity_type: "TASK";
          } & TaskEntitySnapshot)
        | ({
            entity_type: "TASK_UPDATE";
          } & TaskUpdateEntitySnapshot)
        | ({
            entity_type: "TREATMENT_REQUEST";
          } & TreatmentRequestEntitySnapshot)
      )
    | null;
  new_snapshot?:
    | (
        | ({
            entity_type: "CASE_EXPENSE";
          } & CaseExpenseEntitySnapshot)
        | ({
            entity_type: "COVERAGE";
          } & CoverageEntitySnapshot)
        | ({
            entity_type: "DOCUMENT";
          } & DocumentEntitySnapshot)
        | ({
            entity_type: "PROVIDER";
          } & ProviderEntitySnapshot)
        | ({
            entity_type: "STAFF_NOTE";
          } & StaffNoteEntitySnapshot)
        | ({
            entity_type: "TASK";
          } & TaskEntitySnapshot)
        | ({
            entity_type: "TASK_UPDATE";
          } & TaskUpdateEntitySnapshot)
        | ({
            entity_type: "TREATMENT_REQUEST";
          } & TreatmentRequestEntitySnapshot)
      )
    | null;
  status: EntityChangeStatus;
  change_type: EntityChangeType;
  changed_fields?: AugmentedPatchOp[];
  event_cursor_id: string | string;
  created_at: string;
  modified_at: string;
  document_sync_info?: DocumentSyncInfo | null;
};
export type EntityChangesAuditLogSchema = {
  id: string | string;
  entity_change_id: string | string;
  actor?: AgentType;
  user_id?: (string | string) | null;
  timestamp: string;
  previous_status: EntityChangeStatus;
  new_status: EntityChangeStatus;
};
export type BulkEntityChangeStatusResponseSchema = {
  updated_count: number;
};
export type BulkEntityChangeStatusRequestSchema = {
  entity_change_ids: (string | string)[];
  action: EntityChangeStatus;
};
export type EntityChangesFilterOptionSchema = {
  id: string;
  name: string;
};
export type EntityChangesFilterOptionsSchema = {
  matters: EntityChangesFilterOptionSchema[];
  firms: EntityChangesFilterOptionSchema[];
  matter_statuses: EntityChangesFilterOptionSchema[];
  case_managers: EntityChangesFilterOptionSchema[];
};
export type MatterEntityChangesDetailSchema = {
  matter_id: string | string;
  matter_name: string;
  firm_name: string;
  providers: EntityChangeSchema[];
  treatment_requests: EntityChangeSchema[];
  coverages: EntityChangeSchema[];
  tasks: EntityChangeSchema[];
  task_updates: EntityChangeSchema[];
  staff_notes: EntityChangeSchema[];
  documents: EntityChangeSchema[];
  case_expenses: EntityChangeSchema[];
  blob_sink_enabled?: boolean;
};
export type BulkBlobSinkActionRequestSchema = {
  action_ids: string[];
};
export type BulkBlobSinkActionRejectRequestSchema = {
  action_ids: string[];
  reason?: string | null;
};
export type BulkBlobSinkActionResponseSchema = {
  processed_count: number;
  failed_ids?: string[];
  errors?: string[];
};
export type UpdateBlobSinkActionTargetSchema = {
  target_path?: string | null;
  target_name?: string | null;
};
export type BlobSinkActionSchema = {
  id: string;
  status: BlobSinkActionStatus;
  blob_id: string;
  target_system: BlobSinkActionTargetSystem;
  target_path?: string | null;
  target_name?: string | null;
  approval_mode: BlobSinkApprovalMode;
  approval_status: BlobSinkApprovalStatus;
  metadata: Record<string, unknown>;
  created_at?: string | null;
  modified_at?: string | null;
};
export const {
  useEntityChangesListQuery,
  useEntityChangesRetrieveQuery,
  useEntityChangesAuditLogsQuery,
  useEntityChangesBulkUpdateStatusMutation,
  useEntityChangesFilterOptionsQuery,
  useEntityChangesMatterDetailQuery,
  useEntityChangesBlobSinkActionsApproveMutation,
  useEntityChangesBlobSinkActionsRejectMutation,
  useEntityChangesBlobSinkActionsRetryMutation,
  useEntityChangesBlobSinkActionUpdateTargetMutation,
} = injectedRtkApi;
